import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'react-date-range';
import { addMonths, format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Utils
import cn from '../../../../utils/cn';
import {
  parseCellValueToDateInput,
  parseCellValueToDatetimeLocalInput,
  parseCellValueToDate,
} from './dateTimeFieldUtils';

// Components
import Picto from '../../../atoms/Picto/Picto';

// Styles
import styles from './DateTimeField.module.css';

const CALENDAR_MENU_GAP = 4;
const CALENDAR_MENU_MIN_WIDTH = 300;
const CALENDAR_MENU_MAX_HEIGHT = 360;
const CALENDAR_MENU_Z_INDEX = 9999;
/**
 * Fixed position for the portaled calendar menu, anchored to the input.
 * @param {DOMRect} rect
 * @returns {React.CSSProperties}
 */
function getCalendarMenuStyle(rect) {
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openAbove = spaceBelow < CALENDAR_MENU_MAX_HEIGHT && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    top: openAbove ? rect.top - CALENDAR_MENU_GAP : rect.bottom + CALENDAR_MENU_GAP,
    left: rect.left,
    width: Math.max(rect.width, CALENDAR_MENU_MIN_WIDTH),
    minWidth: CALENDAR_MENU_MIN_WIDTH,
    maxHeight: CALENDAR_MENU_MAX_HEIGHT,
    zIndex: CALENDAR_MENU_Z_INDEX,
    transform: openAbove ? 'translateY(-100%)' : undefined,
  };
}

/**
 * Month navigator matching global filter date pickers.
 * @param {Date} currentFocusedDate
 * @param {(value: Date | number | string, mode?: 'set' | 'setMonth' | 'setYear' | 'monthOffset') => void} changeShownDate
 * @returns {JSX.Element}
 */
function renderCalendarNavigator(currentFocusedDate, changeShownDate) {
  return (
    <div className={styles['calendar-rdr-navigator']}>
      <button
        type="button"
        className={styles['calendar-rdr-nav-button']}
        aria-label="Mois précédent"
        onClick={() => changeShownDate(addMonths(currentFocusedDate, -1), 'set')}
      >
        <Picto icon="chevron-left-range" className={styles['calendar-rdr-nav-icon']} />
      </button>
      <button
        type="button"
        className={styles['calendar-rdr-nav-button']}
        aria-label="Mois suivant"
        onClick={() => changeShownDate(addMonths(currentFocusedDate, 1), 'set')}
      >
        <Picto icon="chevron-right-range" className={styles['calendar-rdr-nav-icon']} />
      </button>
    </div>
  );
}

/**
 * BatchActionDateTimeField - Generic date/datetime/time input component for batch actions
 * @param {object} props
 * @param {'date' | 'datetime-local' | 'time'} props.type - Type of input
 * @param {string} [props.value] - Value (ISO string for date/datetime, HH:mm for time)
 * @param {(value: string) => void} props.onChange - Change handler
 * @param {(e: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Blur handler
 * @param {(e: React.KeyboardEvent<HTMLInputElement>) => void} [props.onKeyDown] - Key down handler
 * @param {(value: string) => void} [props.onDaySelect] - Commit handler when a day is picked (table direct-edit)
 * @param {boolean} [props.withCalendarDropdown=false] - Portaled calendar for direct-edit dates (no native picker)
 * @param {string} [props.name] - Input name attribute
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.readOnly] - Read-only (posted, not user-editable)
 * @param {boolean} [props.required] - Whether input is required
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @param {'default' | 'light' | 'table'} [props.theme = 'default'] - Theme of the datetime field
 * @param {string} [props.placeholder] - Placeholder text
 * @returns {JSX.Element} BatchActionDateTimeField component
 */
function DateTimeField({
  type = 'date',
  value = '',
  onChange,
  onBlur,
  onKeyDown,
  onDaySelect,
  withCalendarDropdown = false,
  name,
  disabled = false,
  readOnly = false,
  required = false,
  error = '',
  className = '',
  theme = 'default',
  placeholder = '',
}) {
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const skipNextBlurRef = useRef(false);
  const didOpenCalendarOnMountRef = useRef(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [shownDate, setShownDate] = useState(
    () => parseCellValueToDate(value) ?? new Date(),
  );

  const useCalendarDropdown = type === 'date' && withCalendarDropdown;

  /**
   * Format value for input
   * @param {string} val - Value to format
   * @returns {string} Formatted input value
   */
  const formatInputValue = (val) => {
    if (!val) return '';

    if (type === 'time') {
      return val;
    }

    if (type === 'datetime-local') {
      return parseCellValueToDatetimeLocalInput(val);
    }

    return parseCellValueToDateInput(val);
  };

  const inputValue = formatInputValue(value);
  const selectedDate = useMemo(() => parseCellValueToDate(value), [value]);
  const isEmpty = !value || value === '';
  const inputType = useCalendarDropdown ? 'text' : type;

  useEffect(() => {
    if (!useCalendarDropdown || disabled || didOpenCalendarOnMountRef.current) return;
    didOpenCalendarOnMountRef.current = true;
    setIsCalendarOpen(true);
  }, [useCalendarDropdown, disabled]);

  useLayoutEffect(() => {
    if (!useCalendarDropdown || !isCalendarOpen || disabled) {
      setMenuStyle(null);
      return undefined;
    }

    const updateMenuStyle = () => {
      const input = inputRef.current;
      if (!input) return;
      setMenuStyle(getCalendarMenuStyle(input.getBoundingClientRect()));
    };

    /**
     * @param {Event} event
     */
    const handleScroll = (event) => {
      const { target } = event;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      updateMenuStyle();
    };

    updateMenuStyle();
    window.addEventListener('resize', updateMenuStyle);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuStyle);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [disabled, isCalendarOpen, useCalendarDropdown]);

  /**
   * Handle change event for input
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
   */
  const handleChange = (e) => {
    if (readOnly || useCalendarDropdown) return;
    const newValue = e.target.value;

    if (type === 'time') {
      onChange(newValue);
      return;
    }

    if (newValue) {
      onChange(newValue);
    } else {
      onChange('');
    }
  };

  /**
   * @param {string} isoDate
   */
  const handleDaySelect = useCallback((isoDate) => {
    onChange(isoDate);
    setIsCalendarOpen(false);
    if (onDaySelect) {
      skipNextBlurRef.current = true;
      onDaySelect(isoDate);
    }
  }, [onChange, onDaySelect]);

  /**
   * @param {Date} day
   */
  const handleCalendarChange = useCallback((day) => {
    handleDaySelect(format(day, 'yyyy-MM-dd'));
  }, [handleDaySelect]);

  /**
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleBlur = (e) => {
    if (skipNextBlurRef.current) {
      skipNextBlurRef.current = false;
      return;
    }
    if (useCalendarDropdown) {
      setIsCalendarOpen(false);
    }
    onBlur?.(e);
  };

  const handleInputFocus = () => {
    if (useCalendarDropdown && !disabled) {
      setIsCalendarOpen(true);
    }
  };

  // Native picker opens on a click anywhere in the field, not only on its icon
  const handleInputClick = () => {
    if (useCalendarDropdown || disabled || readOnly) return;
    inputRef.current?.showPicker?.();
  };

  return (
    <div className={cn([styles.wrapper, error && styles['has-error'], className])}>
      <input
        ref={inputRef}
        type={inputType}
        name={name}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleInputFocus}
        onClick={handleInputClick}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        readOnly={readOnly || useCalendarDropdown}
        required={required}
        placeholder={placeholder}
        className={cn([
          styles.input,
          styles[theme],
          isEmpty && styles['input-empty'],
          disabled && styles['input-disabled'],
          readOnly && styles['input-read-only'],
          useCalendarDropdown && styles['input-calendar'],
        ])}
        role={useCalendarDropdown ? 'combobox' : undefined}
        aria-expanded={useCalendarDropdown ? isCalendarOpen : undefined}
        aria-haspopup={useCalendarDropdown ? 'dialog' : undefined}
      />

      {useCalendarDropdown && isCalendarOpen && !disabled && menuStyle
        ? createPortal(
          // Prevent input blur before calendar interactions (month arrows, day pick).
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <div
            ref={menuRef}
            className={styles['calendar-rdr']}
            style={menuStyle}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={format(shownDate, 'MMMM yyyy', { locale: fr })}
          >
            <Calendar
              locale={fr}
              date={selectedDate ?? undefined}
              shownDate={shownDate}
              onShownDateChange={setShownDate}
              displayMode="date"
              onChange={handleCalendarChange}
              months={1}
              direction="horizontal"
              weekStartsOn={1}
              showDateDisplay={false}
              showMonthAndYearPickers={false}
              navigatorRenderer={renderCalendarNavigator}
              dragSelectionEnabled={false}
              showPreview={false}
            />
          </div>,
          document.body,
        )
        : null}

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default DateTimeField;
